/**
 * Single row in the LoginsPanel: display info + the one applicable action
 * button for this login's type/mode/status.
 */

import type { ReactElement } from 'react';
import type { LoginStatus } from '../types';
import { LoginStatusPill } from './LoginStatusPill';

type PrimaryAction = 'direct' | 'brokered-reauth' | 'static' | null;

function primaryActionFor(login: LoginStatus): PrimaryAction {
  if (login.type === 'static') return 'static';
  if (login.type === 'oidc' && login.acquisition_mode === 'direct') return 'direct';
  if (login.type === 'oidc' && login.acquisition_mode === 'brokered' && login.status !== 'connected') {
    return 'brokered-reauth';
  }
  return null;
}

interface LoginRowProps {
  login: LoginStatus;
  onDirectLogin: (login: LoginStatus) => void;
  onReauthenticate: () => void;
  onSetSecret: (login: LoginStatus) => void;
  onDisconnect: (login: LoginStatus) => void;
  isStartingLogin: boolean;
  isDisconnecting: boolean;
}

export function LoginRow({
  login,
  onDirectLogin,
  onReauthenticate,
  onSetSecret,
  onDisconnect,
  isStartingLogin,
  isDisconnecting,
}: LoginRowProps): ReactElement {
  const action = primaryActionFor(login);
  const showDisconnect = login.status === 'connected' && login.type !== 'main_session';
  // A direct provider keeps its button once connected — unlike the brokered
  // one, re-running the flow is the only way to pick up a changed client_id
  // or scope set without disconnecting first, and a stored grant can go
  // stale while the pill still reads Connected (the per-environment OAuth
  // apps CI creates are deleted and recreated with the environment). But
  // "Log in" is the wrong word for it by then.
  const directLabel = login.status === 'not_connected' ? 'Log in' : 'Reconnect';

  return (
    <div className="logins-panel__row">
      <div className="logins-panel__info">
        <span className="logins-panel__name">{login.display_name}</span>
        {login.identity && <span className="logins-panel__identity">{login.identity}</span>}
        <LoginStatusPill login={login} />
        {login.features.length > 0 && (
          <span className="logins-panel__features">Used by: {login.features.join(', ')}</span>
        )}
        {login.git_servers.length > 0 && (
          <span className="logins-panel__features">
            Git servers: {login.git_servers.join(', ')}
          </span>
        )}
      </div>
      <div className="logins-panel__actions">
        {action === 'direct' && (
          <button
            type="button"
            className="logins-panel__btn"
            onClick={() => { onDirectLogin(login); }}
            disabled={isStartingLogin}
          >
            {directLabel}
          </button>
        )}
        {action === 'brokered-reauth' && (
          <div className="logins-panel__reauth">
            <button type="button" className="logins-panel__btn" onClick={onReauthenticate}>
              Re-authenticate
            </button>
            <span className="logins-panel__hint">Managed by your identity provider</span>
          </div>
        )}
        {action === 'static' && (
          <button
            type="button"
            className="logins-panel__btn"
            onClick={() => { onSetSecret(login); }}
          >
            Set secret
          </button>
        )}
        {showDisconnect && (
          <button
            type="button"
            className="logins-panel__btn logins-panel__btn--secondary"
            onClick={() => { onDisconnect(login); }}
            disabled={isDisconnecting}
          >
            Disconnect
          </button>
        )}
      </div>
    </div>
  );
}
